import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import BigModal from '../../../../components/Modals/BigModal'
import AnDateField from '../../../../components/DateTime/DateField'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
import { FREQUENCY_MONTHS } from '../../../../helpers/mandatoryTrainingStatus'
import DocumentsField from '../../../../helpers/DocumentsField'
import { uploadDocument } from '../../../../helpers/uploadDocument'

// Add or edit one training record for an employee (Personnel side). Pass
// `record` to edit; pass `course` to preselect the course when adding.
// The certificate goes to S3 through the shared uploader, then its key is
// saved on the record. If the upload fails (demo tenants refuse S3 writes)
// the record is still saved without the file and a toast says so.
const MandatoryTrainingRecordDialog = ({
  open,
  onClose,
  employee,
  courses = [],
  course = null,
  record = null,
  onSaved,
}) => {
  const isEdit = !!record
  const { setToast } = useContext(GlobalContext)

  const [courseID, setCourseID] = useState('')
  const [date, setDate] = useState(() => moment())
  const [note, setNote] = useState('')
  const [files, setFiles] = useState([])
  const [removedDocuments, setRemovedDocuments] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // Reset the form each time the dialog opens for a different target
  useEffect(() => {
    if (!open) return
    setCourseID(record?.course ?? course?._id ?? '')
    setDate(record?.date ? moment(record.date) : moment())
    setNote(record?.note ?? '')
    setFiles([])
    setRemovedDocuments([])
    setError(null)
  }, [open, record, course])

  const selectedCourse = courses.find((c) => c._id === courseID)
  const clinicalRole = employee?.employee?.clinicalRole
  const requiredForRole = !!selectedCourse?.requiredBy?.[clinicalRole]
  const validMonths = FREQUENCY_MONTHS[selectedCourse?.frequency]
  const expiresOn =
    validMonths && date?.isValid?.()
      ? moment(date).add(validMonths, 'months')
      : null

  const canSubmit =
    !!courseID &&
    !!date &&
    date.isValid?.() &&
    !date.isAfter(moment(), 'day') &&
    !saving

  const toggleRemove = (key) =>
    setRemovedDocuments((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    )

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!canSubmit) return
    setSaving(true)
    setError(null)
    try {
      let uploadedKey
      if (files[0]) {
        try {
          uploadedKey = await uploadDocument(
            files[0],
            'employees/mandatorytraining',
          )
        } catch (err) {
          setToast({
            message: `${err.message} The record has been saved without a certificate.`,
            severity: 'warning',
          })
        }
      }

      let res
      if (isEdit) {
        const body = { date: date.toISOString(), note }
        if (uploadedKey) body.certificate = uploadedKey
        else if (
          record.certificate &&
          removedDocuments.includes(record.certificate)
        )
          body.certificate = null
        res = await API.put(
          `/employees/updateTrainingRecord/${record._id}`,
          body,
        )
      } else {
        res = await API.post('/employees/addTrainingRecord', {
          employee: employee.employee._id,
          course: courseID,
          date: date.toISOString(),
          note,
          certificate: uploadedKey,
          requiresApproval: false,
        })
      }
      if (res.status === 200) {
        onSaved?.(res.data)
      } else {
        setError(
          res.data?.error ||
            'Unable to save this training record. Please try again.',
        )
      }
    } catch (e) {
      console.error(e)
      setError('Unable to save this training record. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <BigModal
      open={open}
      onClose={onClose}
      autoWidth
      style={{ width: { xs: 'calc(100vw - 24px)', sm: 480 }, padding: 3 }}
    >
      <Stack gap={2} component="form" onSubmit={handleSubmit}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="start"
        >
          <Stack>
            <Typography variant="title3" fontWeight={700}>
              {isEdit ? 'Edit Training Record' : 'Add Training Record'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {employee?.firstName} {employee?.lastName}
              {clinicalRole ? ` · ${clinicalRole}` : ''}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>

        <TextField
          select
          label="Course"
          value={courseID}
          onChange={(e) => setCourseID(e.target.value)}
          required
          disabled={isEdit}
          fullWidth
          helperText={
            selectedCourse
              ? `${selectedCourse.frequency ?? 'No renewal frequency set'}${
                  requiredForRole
                    ? ' · required for this role'
                    : ' · not required for this role'
                }`
              : 'Choose the course this record is evidence for'
          }
        >
          {courses.map((option) => (
            <MenuItem key={option._id} value={option._id}>
              {option.title}
              {option.requiredBy?.[clinicalRole] ? '' : '  (not required)'}
            </MenuItem>
          ))}
        </TextField>

        <AnDateField
          date={date}
          onChange={(newDate) => setDate(newDate)}
          label="Date Completed"
          required
          disableFuture
          fullWidth
          helperText={
            expiresOn
              ? `Valid until ${expiresOn.format('Do MMM YYYY')}`
              : undefined
          }
        />

        <TextField
          label="Notes"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Classroom session, external provider, certificate number"
          multiline
          minRows={2}
          fullWidth
        />

        <Stack gap={1}>
          <Typography variant="body2" fontWeight={600}>
            Certificate
          </Typography>
          <DocumentsField
            existingDocuments={record?.certificate ? [record.certificate] : []}
            removedDocuments={removedDocuments}
            onToggleRemove={toggleRemove}
            existingLabel={() => 'Current certificate'}
            files={files}
            onFilesChange={setFiles}
            single
            accept="application/pdf,image/*"
            caption="Optional — PDF or image of the certificate, up to 10 MB"
            acceptLabel="PDF or image files"
            maxSizeMB={10}
            disabled={saving}
          />
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Button onClick={onClose} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={!canSubmit}
          >
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Record'}
          </Button>
        </Stack>
      </Stack>
    </BigModal>
  )
}

export default MandatoryTrainingRecordDialog
